import { Array, pipe } from 'effect'
import { isNotNullish } from 'effect/Predicate'

import { PRESET_MODELS } from '../../../../constant/presetModels.ts'
import type { ViewQueryAggregation, ViewQueryList } from '../../../../schemas/query/index.ts'
import type { ViewUISheet } from '../../../../schemas/ui/sheet.ts'
import type { ViewConfigSheet } from '../../../../schemas/view_config/index.ts'
import { flatNodes } from '../../../query/executeViewQuery/util.ts'
import type { CompileContext } from '../../common.ts'
import { updateConfigByAdditionalConfig } from '../kpi/updateConfigByAdditionalConfig.ts'
import { compileSheetAndMeasure } from './compileSheetAndMeasure.ts'
import { compileSheetViewConfigFields } from './compileSheetViewConfigFields.ts'
import { compileSheetViewConfigFilterTree } from './compileSheetViewConfigFilterTree.ts'
import { compileSheetViewConfigSorters } from './compileSheetViewConfigSorters.ts'
import { compileSheetViewConfigTree } from './compileSheetViewConfigTree.ts'
import { expandChildrenByReferenceField } from './expandChildrenByReferenceField.ts'
import { pruneSheetConfigTree } from './pruneSheetConfigTree.ts'

export interface CompiledSheetViewConfig {
  type: 'sheet'
  queries: [ViewQueryList, ...ViewQueryAggregation[]]
  query: ViewQueryList
  ui: [ViewUISheet]
  context: CompileContext
}

// eslint-disable-next-line sonarjs/todo-tag
// TODO
const userStream = {
  name: `salescore_users`,
  destination: {
    name: `salescore_users`,
  },
  meta: {
    label: `ユーザー`,
  },
}
const emptyQuery: ViewQueryList = {
  type: 'list',
  tree: {
    name: userStream.name,
    path: [userStream.name],
    read: {
      // eslint-disable-next-line sonarjs/todo-tag
      idColumn: 'id', // TODO: idが常にある前提。カスタムストリームなど、idがない場合にどうするか？destinationで指定すべきか？
      tableSql: userStream.destination.name,
      tableSqlTransparency: 'transparent',
      tableType: 'table',
    },
    write: {
      streamName: userStream.name,
    },
    meta: {
      label: userStream.meta.label,
      dependedStreamNames: [userStream.name],
    },
  },
  fields: [],
  sorters: [],
  filterTree: {
    logicalOperator: 'and',
    leafs: [],
    children: [],
  },
}

export function compileSheetViewConfig(configRaw: ViewConfigSheet, context: CompileContext): CompiledSheetViewConfig {
  const empty: CompiledSheetViewConfig = {
    type: 'sheet',
    queries: [emptyQuery], // 本来はここを空にしたいが、初期設計のミスで空が許されないため、適当な値をいれている
    query: emptyQuery,
    ui: [
      {
        type: 'Sheet' as const,
        columns: [],
        fixedLeftColumnIndex: undefined,
        visibleRowNum: undefined,
      },
    ],
    context,
  }
  const config: ViewConfigSheet = updateConfigByAdditionalConfig(configRaw, context)
  const prunedTree = pruneSheetConfigTree(config)
  const expandedTree = expandChildrenByReferenceField(prunedTree, config, context)
  const tree = compileSheetViewConfigTree(expandedTree, context)
  if (tree === undefined || expandedTree === undefined) {
    return empty
  }
  const queryNodes = flatNodes(tree)
  const configNodes = flatNodes(expandedTree)
  const fieldsAndColumns = compileSheetViewConfigFields(config.fields, queryNodes, configNodes, context)
  if (fieldsAndColumns.length === 0) {
    return empty
  }
  const fields = fieldsAndColumns.map((x) => x.queryField)
  const listQuery: ViewQueryList = {
    type: 'list',
    tree,
    fields,
    filterTree: compileSheetViewConfigFilterTree(
      config.filterTree,
      queryNodes,
      configNodes,
      fields,
      context,
      undefined,
      config,
    ),
    sorters: compileSheetViewConfigSorters(config, queryNodes, fieldsAndColumns, context),
    extra: {
      searchQuery: config.meta?.searchQuery,
      snapshotConfigs: pipe(
        fieldsAndColumns.flatMap((x) => x.snapshotConfigs),
        Array.dedupeWith((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      ), // 雑にdedupeWithしておく
    },
  }
  const measures = (config.fields ?? []).flatMap((field) => field.measures ?? [])
  const aggregationQueries = measures
    .map((measure) => compileSheetAndMeasure(measure, config, undefined, undefined, context))
    .filter(isNotNullish)
  const queries: [ViewQueryList, ...ViewQueryAggregation[]] = [
    listQuery,
    ...aggregationQueries.map((x) => x.query).filter(isNotNullish),
  ]

  const columns = fieldsAndColumns
    .filter(
      (x) =>
        // eslint-disable-next-line sonarjs/todo-tag
        // TODO: addSalescoreUserFieldsにより、salescore_users_with_groupのid系フィールドが追加されるが、表示の時は不要なのでやや無理やりここで弾いている
        !(
          x.configField.property.modelName === PRESET_MODELS.USER_WITH_USER_GROUP.NAME &&
          x.configField.property.propertyName.endsWith('id')
        ),
    )
    .map((x) => x.column)

  return {
    type: 'sheet',
    queries,
    query: queries[0],
    ui: [
      {
        type: 'Sheet',
        columns,
        fixedLeftColumnIndex: config.meta?.fixedLeftColumnIndex,
        visibleRowNum: config.meta?.visibleRowNum,
        isGoalView: config.meta?.isGoalView,
      },
    ],
    context,
  }
}
